// ------------------------------------------------------------------------------
// name: useFetchingDelay
// author: mudas
// created: 2025/8/19
// ------------------------------------------------------------------------------

import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useDebounceFn } from 'ahooks';

import { FetchStatus } from '@tanstack/query-core';

export interface FetchingDelayOptions {
  // 针对 isFetching 的延迟生效
  fetchingDelay?: number;
}

export interface DataOptions {
  fetchStatus: FetchStatus;
}

/**
 * 让 useQuery 支持 `isFetching` 延迟变更，适用于快速返回响应结果时的闪烁问题
 * 效果参见 ahooks/useRequest 的 `loadingDelay`
 */
export const useFetchingDelay = (options: DataOptions & FetchingDelayOptions) => {
  const { fetchStatus, fetchingDelay } = options;

  const [isFetching, setIsFetching] = useState<boolean>();

  const handler = useDebounceFn(
    () => {
      setIsFetching(true);
    },
    {
      wait: fetchingDelay,
      leading: false,
      trailing: true,
    },
  );

  useEffect(() => {
    if (fetchStatus === 'fetching') {
      handler.run();
    } else if (fetchStatus === 'idle') {
      handler.cancel();
      setIsFetching(false);
    }
    return handler.cancel;
  }, [fetchStatus]);

  return [isFetching, setIsFetching] as [boolean, Dispatch<SetStateAction<boolean>>];
};
